import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';

export interface FABSlot {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  tinted?: boolean;
  badge?: string;
  contextReason?: string;
}

export interface FloatingActionButtonProps {
  slots: [FABSlot, FABSlot, FABSlot];
  layout: 'vertical' | 'narrow';
  className?: string;
  bottomOffset?: number;
  suppressed?: boolean;
  /**
   * Start collapsed to the edge tab on this route (e.g. safety pages) without touching the user's saved
   * stash preference. A swipe expands/collapses for this route only; leaving it restores the saved state.
   */
  collapsedOnRoute?: boolean;
  /** Identifies the current route; a change resets the per-route override. */
  routeKey?: string;
}


// ─── Visual constants (from helm-fab.jsx design spec) ─────────────────────────

const TRAY_BG = 'rgba(26,34,51,0.72)';
const TRAY_BORDER = 'rgba(255,255,255,0.08)';
const TRAY_SHADOW =
  '0 12px 24px rgba(0,0,0,0.45), 0 4px 10px rgba(0,0,0,0.35), 0 0 0 0.5px rgba(255,255,255,0.04)';
const TINTED_BG = 'rgba(27,108,168,0.18)';
const TINTED_RING = '0 0 0 1px rgba(27,108,168,0.55)';
const PRIMARY_HI_BORDER = '0 0 0 1px rgba(255,255,255,0.14)';
const BADGE_BG = 'var(--theme-warning)';
const BADGE_SURFACE = 'var(--theme-fab-surface)';

// When stashed, the tray slides right so only this many px of the rounded edge peek out
const PEEK_PX = 10;

// ─── Slot button ──────────────────────────────────────────────────────────────

interface SlotButtonProps {
  slot: FABSlot;
  isPrimary: boolean;
  isCompact: boolean;
  reducedMotion: boolean;
  contextReasonId?: string;
}

function SlotButton({ slot, isPrimary, isCompact, reducedMotion, contextReasonId }: SlotButtonProps) {
  const size = isCompact ? 44 : isPrimary ? 56 : 48;
  const iconSize = isCompact ? 22 : isPrimary ? 24 : 22;
  const borderRadius = isCompact ? 12 : isPrimary ? 18 : 14;

  const bg = slot.tinted
    ? TINTED_BG
    : !isCompact && isPrimary
    ? 'var(--theme-fab-surface-raised)'
    : 'transparent';

  const iconColor = slot.tinted ? 'var(--theme-primary)' : 'var(--theme-fab-icon)';
  const boxShadow = slot.tinted
    ? TINTED_RING
    : !isCompact && isPrimary
    ? PRIMARY_HI_BORDER
    : 'none';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <button
        type="button"
        aria-label={slot.label}
        aria-describedby={slot.tinted && contextReasonId ? contextReasonId : undefined}
        onClick={slot.onClick}
        style={{
          position: 'relative',
          width: size,
          height: size,
          minWidth: 44,
          minHeight: 44,
          background: bg,
          border: 'none',
          borderRadius,
          boxShadow,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
          flexShrink: 0,
          WebkitTapHighlightColor: 'transparent',
          transition: reducedMotion ? 'none' : 'background 160ms ease, transform 100ms ease',
        }}
      >
        {/* Scaled icon wrapper */}
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: iconSize,
            height: iconSize,
          }}
          aria-hidden="true"
        >
          {slot.icon}
        </span>

        {/* Badge */}
        {slot.badge != null && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: isPrimary && !isCompact ? -2 : -1,
              right: isPrimary && !isCompact ? -2 : -1,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 8,
              background: BADGE_BG,
              color: 'var(--theme-text-inverse)',
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 0 2px ${BADGE_SURFACE}`,
              lineHeight: 1,
            }}
          >
            {slot.badge}
          </span>
        )}
      </button>

      {/* Hidden context reason for aria-describedby */}
      {slot.tinted && contextReasonId && slot.contextReason && (
        <span id={contextReasonId} style={{ display: 'none' }}>
          {slot.contextReason}
        </span>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function FloatingActionButton({ slots, layout, className = '', bottomOffset = 0, suppressed = false, collapsedOnRoute = false, routeKey }: FloatingActionButtonProps) {
  const isMobile = useIsMobile();

  // Global stash state — persists across page navigations via localStorage
  const [stashed, setStashed] = useState(() => {
    try { return localStorage.getItem('helm:fab:stashed') === '1'; } catch { return false; }
  });

  // Per-route override while collapsedOnRoute: null = follow the route default (collapsed).
  // Never persisted, and reset on every route change.
  const [routeExpanded, setRouteExpanded] = useState<boolean | null>(null);
  useEffect(() => { setRouteExpanded(null); }, [routeKey, collapsedOnRoute]);
  const effectiveStashed = collapsedOnRoute ? routeExpanded !== true : stashed;

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  // Swipe tracking
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const SWIPE_THRESHOLD = 40; // px of horizontal movement to trigger stash toggle

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // Must be more horizontal than vertical, and exceed threshold
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dy) > Math.abs(dx)) return;
    const swipeRight = dx > 0;
    const shouldStash = swipeRight && !effectiveStashed;
    const shouldRestore = !swipeRight && effectiveStashed;
    if (collapsedOnRoute) {
      if (shouldStash || shouldRestore) setRouteExpanded(shouldRestore);
      return;
    }
    if (shouldStash || shouldRestore) {
      const next = !stashed;
      setStashed(next);
      try { localStorage.setItem('helm:fab:stashed', next ? '1' : '0'); } catch {}
    }
  }, [stashed, effectiveStashed, collapsedOnRoute]);

  // Mobile only
  if (!isMobile) return null;

  const isVertical = layout === 'vertical';
  const isCompact = !isVertical;

  const transitionProp = prefersReducedMotion.current
    ? 'none'
    : 'transform 240ms cubic-bezier(0.4,0,0.2,1), opacity 200ms ease';

  const bottomPos = `calc(16px + ${bottomOffset}px + env(safe-area-inset-bottom, 0px))`;

  // Tray container styles
  const trayStyle: React.CSSProperties = isVertical
    ? {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: 6,
        background: TRAY_BG,
        backdropFilter: 'blur(14px) saturate(160%)',
        WebkitBackdropFilter: 'blur(14px) saturate(160%)',
        border: `1px solid ${TRAY_BORDER}`,
        borderRadius: 36,
        boxShadow: TRAY_SHADOW,
      }
    : {
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        padding: 5,
        background: TRAY_BG,
        backdropFilter: 'blur(14px) saturate(160%)',
        WebkitBackdropFilter: 'blur(14px) saturate(160%)',
        border: `1px solid ${TRAY_BORDER}`,
        borderRadius: 26,
        boxShadow: TRAY_SHADOW,
      };

  // When stashed or suppressed: slide right so only PEEK_PX of the rounded edge is visible.
  const stashTransform = effectiveStashed || suppressed
    ? `translateX(calc(100% - ${PEEK_PX}px))`
    : 'translateX(0)';

  return (
    <div
      role="toolbar"
      aria-label="Quick actions"
      aria-hidden={effectiveStashed || suppressed}
      className={className}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'fixed',
        right: 16,
        bottom: bottomPos,
        zIndex: 40,
        // Touch area slightly wider than the tray for easier swipe-back when stashed
        touchAction: 'pan-y',
        transform: stashTransform,
        transition: transitionProp,
      }}
    >
      {/* Main tray — the rounded edge that peeks out when stashed acts as the grab target */}
      <div style={trayStyle}>
        {/* While collapsed or suppressed the slot buttons are inert (not tappable, not focusable); the tray edge
            behind them still receives the swipe that brings the rail back. display:contents keeps the tray layout. */}
        <div
          data-testid="fab-slots"
          style={{ display: 'contents' }}
          {...((effectiveStashed || suppressed) ? ({ inert: '' } as Record<string, string>) : {})}
        >
        {slots.map((slot, index) => (
          <SlotButton
            key={slot.key}
            slot={slot}
            isPrimary={index === 0}
            isCompact={isCompact}
            reducedMotion={prefersReducedMotion.current}
            contextReasonId={slot.tinted ? `fab-ctx-reason-${slot.key}` : undefined}
          />
        ))}
        </div>
      </div>
    </div>
  );
}
